// The diary editor's field state.
//
// W54.0 A1 moved it out of `components/diary/DiaryEditor.tsx`, which was
// 24,699 B / 537 lines and therefore over the `AGENTS.md §3 #10` ceiling for
// a component this sprint had to change. The split is mechanical: the same
// `useState` calls, the same queries, the same effects — moved, not rewritten.
//
// **The one behaviour change lives in the draft keys**, and it is the point of
// the exercise: edit-mode drafts are scoped by the diary id
// (`diary-title:edit:<id>`) instead of by the mode alone
// (`diary-title:edit`). See `lib/diary/drafts.ts` for why the old keys are
// invalidated rather than migrated.
//
// The seeding order matters and is unchanged: `initial` wins over a draft, so
// editing a saved diary shows the diary. `initial` is optional because
// create-mode editors have none.

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { listWishes } from "@/lib/api/wishlist";
import { useAlbumList } from "@/lib/hooks/album";
import { useAnniversariesList } from "@/lib/hooks/anniversary";
import { readDraft, useDraft } from "@/lib/hooks/useDraft";
import { diaryDraftKeys, type DiaryDraftMode } from "@/lib/diary/drafts";
import { todayISO } from "@/lib/diary/dates";
import type { DiaryOut, MediaOut } from "@/lib/api/diary";

/**
 * W52.1 D-4 — what an attached photo actually needs here: an id (for
 * `media_ids`, and for the thumbnail) and nothing else.
 *
 * It used to be `MediaOut`, because the old `PhotoUploader` path returned the
 * full row from `POST /media`. The album path returns `MediaRef` instead —
 * `{ id, url, mime_type, width, height }` — which has no `file_path`,
 * `size_bytes`, `uploaded_by` or `uploaded_at`. Widening `MediaRef` or filling
 * those fields with guesses would have been worse than not needing them: the
 * thumbnail is rendered from the id by the same helper the album grid uses,
 * so there is nothing left to fabricate.
 */
export interface DiaryAttachment {
  id: string;
}

/** The slice of a saved diary the editor seeds itself from. */
export type DiaryFormInitial = Pick<
  DiaryOut,
  "date" | "title" | "content" | "mood" | "is_private"
>;

export interface UseDiaryFormStateArgs {
  initial?: DiaryFormInitial;
  mode: DiaryDraftMode;
  /**
   * The diary being edited. `null` in create mode; the draft keys are scoped
   * by it in edit mode.
   */
  diaryId?: string | null;
  attachedMedia?: MediaOut[];
  initialAnniversaryId?: string | null;
}

/** The anniversary options the source picker renders. */
export interface DiarySourceOption {
  id: string;
  title: string;
  date: string;
}

/** The wish options the source picker renders. */
export interface DiaryWishOption {
  id: string;
  name: string;
}

export function useDiaryFormState({
  initial,
  mode,
  diaryId,
  attachedMedia = [],
  initialAnniversaryId = null,
}: UseDiaryFormStateArgs) {
  const draftKeys = useMemo(() => diaryDraftKeys(mode, diaryId), [mode, diaryId]);

  const [date, setDate] = useState(initial?.date ?? todayISO());
  // W39.74 — seeded from the draft in the same pass the editor reads its
  // content, so a session revoked halfway through an entry brings the entry
  // back rather than an empty form. `initial` wins: editing a saved diary must
  // show the diary.
  const [title, setTitle] = useState(() => initial?.title || readDraft(draftKeys.title));
  const [mood, setMood] = useState<string | null>(initial?.mood ?? null);
  const [isPrivate, setIsPrivate] = useState(initial?.is_private ?? false);
  const [media, setMedia] = useState<DiaryAttachment[]>(
    attachedMedia.map((m) => ({ id: m.id })),
  );
  const [formError, setFormError] = useState<string | null>(null);
  // W39.66 — measured on the editor's own text, not on a `characterCount`
  // storage that does not exist here. Held in state so the counter re-renders as
  // the person types; Tiptap emits no subscription for this without the
  // extension, which is not a dependency of this project.
  const [contentLength, setContentLength] = useState(0);
  const [sourceAnniversaryId, setSourceAnniversaryId] = useState<string | null>(
    initialAnniversaryId,
  );
  // W50.3 D-3 — the second half of the source picker. The two are mutually
  // exclusive: the payload carries one `source_type`, so choosing a wish clears
  // the anniversary rather than letting both look set.
  const [sourceWishId, setSourceWishId] = useState<string | null>(null);

  // W39.74 — the session can be revoked under a person who is halfway through
  // writing (signing in on a second phone does it), the redirect to /login
  // unmounts this component, and React state goes with it. Measured: the typed
  // sentence was gone and nothing had been persisted. `sessionStorage` survives
  // the sign-in round trip in the same tab; `localStorage` would leave the words
  // on a shared device, which is why this is not that.
  const titleDraft = useDraft(draftKeys.title);
  const contentDraft = useDraft(draftKeys.content);

  // W52.1 D-4 — the album list, for the same reason `/albums/:id` fetches it.
  // A pair always has the system 「未分類」 album, so this is not an empty
  // state in practice; when it somehow is, the add-photo control is hidden
  // rather than offering a sheet with nothing to choose.
  const albumsQuery = useAlbumList({ enabled: mode === "create" });
  const albumOptions = useMemo(
    () =>
      (albumsQuery.data ?? []).map((a) => ({
        id: a.id,
        name: a.name,
        is_system: a.is_system,
      })),
    [albumsQuery.data],
  );

  const wishesQuery = useQuery({
    queryKey: ["wishes", "list", { sourcePicker: true }],
    queryFn: () => listWishes(),
    enabled: mode === "create",
    staleTime: 30_000,
  });
  const wishesForSource = useMemo<DiaryWishOption[]>(
    () => (mode === "create" ? (wishesQuery.data?.items ?? []) : []),
    [mode, wishesQuery.data],
  );

  // W3.1 — fetch upcoming anniversaries (≤ 1y ahead) for the source picker.
  const upcomingAnniversariesQuery = useAnniversariesList(
    { limit: 50 },
    { enabled: mode === "create" },
  );
  const upcomingAnniversaries = useMemo<DiarySourceOption[]>(() => {
    if (mode !== "create") return [];
    return upcomingAnniversariesQuery.data?.items ?? [];
  }, [mode, upcomingAnniversariesQuery.data]);

  // Auto-select the deep-linked anniversary once the list arrives.
  useEffect(() => {
    if (
      mode === "create" &&
      initialAnniversaryId &&
      upcomingAnniversaries.some((a) => a.id === initialAnniversaryId) &&
      !sourceAnniversaryId
    ) {
      setSourceAnniversaryId(initialAnniversaryId);
    }
  }, [mode, initialAnniversaryId, upcomingAnniversaries, sourceAnniversaryId]);

  useEffect(() => {
    if (initial?.title || !titleDraft.value) return;
    setTitle(titleDraft.value);
    // Mount only: later changes come from the input's own onChange.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    date,
    setDate,
    title,
    setTitle,
    mood,
    setMood,
    isPrivate,
    setIsPrivate,
    media,
    setMedia,
    formError,
    setFormError,
    contentLength,
    setContentLength,
    sourceAnniversaryId,
    setSourceAnniversaryId,
    sourceWishId,
    setSourceWishId,
    albumOptions,
    wishesForSource,
    upcomingAnniversaries,
    titleDraft,
    contentDraft,
  };
}
